import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import WelcomeTour from "@/components/WelcomeTour";
import { startDemoTour } from "@/components/DemoTour";
import { Button } from "@/components/ui/button";
import {
  Sparkles, GanttChartSquare, Network, Map as MapIcon, Kanban, GitCompareArrows, Rocket, CalendarClock, Gauge,
  IdCard, Link2, NotebookPen, FolderOpen, LayoutDashboard, ShieldCheck, BarChart3, PlayCircle, CheckCircle2, BookOpen, Clapperboard,
} from "lucide-react";

const SECTIONS = [
  {
    title: "Build your plan",
    blurb: "Get from idea to a working schedule in minutes.",
    items: [
      { icon: Sparkles, name: "AI plan", tip: "In any project, click AI plan and describe the work — you get phases, tasks, dates and dependencies instantly." },
      { icon: GanttChartSquare, name: "Plan (WBS) & Gantt", tip: "Edit tasks inline, print or export CSV. The Gantt shows the schedule with customizable phase colors." },
      { icon: Network, name: "Visual & Critical path", tip: "A Visio-style map of your plan showing % complete, track status and the critical chain." },
      { icon: MapIcon, name: "Roadmap", tip: "An executive-ready swimlane roadmap with full color and font control — perfect for readouts." },
      { icon: GitCompareArrows, name: "Baseline", tip: "Snapshot the plan; Project Management Orchestrator then tracks what slipped and what improved." },
    ],
  },
  {
    title: "Stay in control",
    blurb: "The tool watches your plan so you don't have to.",
    items: [
      { icon: LayoutDashboard, name: "Project Dashboard", tip: "Every project opens on a control center: due items, risks, true-ups, audit to-dos and quick-add buttons." },
      { icon: Rocket, name: "Pilot Auto Pilot", tip: "Rate each person Fast / Medium / Slow and Pilot advances % complete every working day — capped at 99% until YOU confirm." },
      { icon: CalendarClock, name: "True-ups", tip: "A weekly confirm-or-correct ritual. Every confirmation teaches Pilot each person's real velocity." },
      { icon: Gauge, name: "Forecasts & alerts", tip: "Predicted finish dates per task, plus warnings when work is about to complete or forecast to slip." },
    ],
  },
  {
    title: "Keep everyone aligned",
    blurb: "Less chasing, more doing.",
    items: [
      { icon: IdCard, name: "Cards", tip: "A per-person snapshot of open tasks, action items and true-ups — emailed daily, weekly or monthly." },
      { icon: Link2, name: "Magic update links", tip: "Each card email includes a no-login link where teammates submit their % complete straight into your true-up queue." },
      { icon: NotebookPen, name: "Meeting Notebook", tip: "One tab per meeting. Paste a transcript and AI drafts the minutes and finds the action items for one-click creation." },
      { icon: FolderOpen, name: "Artifacts", tip: "Charters, docs and recordings with categories and version history — re-upload the same file to get v2." },
    ],
  },
  {
    title: "See the big picture",
    blurb: "For you, your leadership, and your auditors.",
    items: [
      { icon: LayoutDashboard, name: "Portfolio", tip: "Health, risks, resource alerts and open cards rolled up across every project, with drill-downs." },
      { icon: ShieldCheck, name: "Audit", tip: "Automatic PM-hygiene scoring (baseline set, risks logged, artifacts filed) with supervisor views and trends." },
      { icon: BarChart3, name: "Status reports", tip: "Compose weekly readouts (AI can draft them) and share public read-only links with stakeholders." },
    ],
  },
];

const CHECKLIST_LINKS = { project: "/dashboard?new=1", baseline: "/dashboard", pilit: "/dashboard", cards: "/cards", trueup: "/dashboard", artifact: "/dashboard" };

export default function Guide() {
  const [tourOpen, setTourOpen] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/me/onboarding").then(({ data }) => setChecklist(data)).catch(() => setChecklist({ steps: [], completed: 0, total: 0 }));
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="overline text-muted-foreground mb-2">Welcome package</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3" data-testid="guide-title">
              <BookOpen className="h-9 w-9 text-primary" /> Getting started
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Everything Project Management Orchestrator can do for you, in one place. New here? Start with the 2-minute tour.</p>
          </div>
          <Button className="gap-2 h-11 shrink-0" onClick={() => setTourOpen(true)} data-testid="guide-replay-tour">
            <PlayCircle className="h-4 w-4" /> Play the welcome tour
          </Button>
          <Button variant="outline" className="gap-2 h-11 shrink-0" onClick={startDemoTour} data-testid="guide-demo-tour">
            <Clapperboard className="h-4 w-4" /> Play the demo tour
          </Button>
        </div>

        {/* Quick start checklist (live) */}
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-6 mb-10" data-testid="guide-checklist">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="font-display font-bold text-lg flex-1">Your first week, step by step</h2>
            {checklist && checklist.total > 0 && (
              <span className="text-xs font-bold text-primary" data-testid="guide-checklist-progress">{checklist.completed} of {checklist.total} done</span>
            )}
          </div>
          {checklist && checklist.total > 0 && (
            <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden mb-5">
              <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${(checklist.completed / checklist.total) * 100}%` }} />
            </div>
          )}
          <ol className="space-y-2.5">
            {(checklist?.steps || []).map((s, i) => (
              <li key={s.key} className="flex items-start gap-3 text-sm" data-testid={`guide-step-${s.key}`}>
                {s.done ? (
                  <CheckCircle2 className="h-6 w-6 text-emerald-500 shrink-0" data-testid={`guide-step-done-${s.key}`} />
                ) : (
                  <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                )}
                <button onClick={() => nav(CHECKLIST_LINKS[s.key] || "/dashboard")} className={`pt-0.5 text-left hover:underline ${s.done ? "text-muted-foreground line-through decoration-emerald-500/40" : ""}`}>
                  {s.label}
                </button>
              </li>
            ))}
            {!checklist && <li className="text-sm text-muted-foreground">Loading your progress…</li>}
          </ol>
          <Button className="mt-5 gap-2" onClick={() => nav("/dashboard?new=1")} data-testid="guide-create-project">
            <Sparkles className="h-4 w-4" /> Create your first project
          </Button>
        </div>

        {/* Feature sections */}
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title} data-testid={`guide-section-${s.title.toLowerCase().replace(/\s/g, "-")}`}>
              <h2 className="font-display font-bold text-xl">{s.title}</h2>
              <p className="text-muted-foreground text-sm mt-1 mb-4">{s.blurb}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.items.map((it) => (
                  <div key={it.name} className="border border-border rounded-xl p-4 flex gap-3 hover:border-primary/40 transition-colors">
                    <it.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm">{it.name}</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{it.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 border-t border-border pt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Tip: the Feedback button (bottom-left) goes straight to the team — bug reports get priority attention.
        </div>
      </div>
      <WelcomeTour open={tourOpen} onOpenChange={setTourOpen} />
    </Layout>
  );
}
